/**
 * Storage Inventory API client.
 *
 * Speaks a stable /inventory/* HTTP contract. Asset CRUD and photos are handled
 * by whatever backend is configured: in production that is API Gateway -> Lambda
 * backed by DynamoDB (assets + settings) and S3 (photos); in local dev it is the
 * dev proxy. Photos are referenced from an asset's `images` array by an opaque
 * key (uploaded via /inventory/photo, displayed via a URL from /inventory/photo-url).
 *
 * This follows the same pattern as apiClient.ts (simpleFetch for dev,
 * SigV4-signed requests for production).
 */

import { simpleFetch } from './apiClient';
import type { Asset, AssetCategory } from '@/types/storageInventory';

export const storageInventoryApi = {
  /**
   * Fetch all items for a category.
   * GET /inventory/scan?category=servers
   */
  scan: (category: AssetCategory | 'settings') =>
    simpleFetch<Asset[]>(`/inventory/scan?category=${category}`),

  /**
   * Create a new asset. The backend generates the UUID.
   * POST /inventory/create - body: { category, item }
   */
  create: (category: AssetCategory, item: Omit<Asset, 'id'>) =>
    simpleFetch<Asset>('/inventory/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, item }),
    }),

  /**
   * Update an existing asset (full replacement by id).
   * POST /inventory/update - body: { category, item }
   */
  update: (category: AssetCategory, item: Asset) =>
    simpleFetch<Asset>('/inventory/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, item }),
    }),

  /**
   * Delete an asset by id.
   * POST /inventory/delete - body: { category, id }
   */
  delete: (category: AssetCategory, id: string) =>
    simpleFetch<{ deleted: string }>('/inventory/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, id }),
    }),

  /**
   * Bulk create multiple assets at once.
   * POST /inventory/bulk - body: { category, items[] }
   */
  bulkCreate: (category: AssetCategory, items: Omit<Asset, 'id'>[]) =>
    simpleFetch<{ count: number }>('/inventory/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, items }),
    }),

  /**
   * Upload a photo blob and get back its storage key.
   * POST /inventory/photo - body: { category, data (base64), contentType, filename }
   * The returned key is stored in an asset's `images` array (not the bytes).
   */
  uploadPhoto: (
    category: AssetCategory,
    photo: { data: string; contentType: string; filename?: string }
  ) =>
    simpleFetch<{ key: string }>('/inventory/photo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category, ...photo }),
    }),

  /**
   * Resolve a stored photo key to a URL the browser can load for display.
   * GET /inventory/photo-url?key=...
   * (Locally this returns a relative /inventory/photo-blob?key=... URL served
   * by the proxy; in production it is a short-lived presigned URL.)
   */
  getPhotoUrl: (key: string) =>
    simpleFetch<{ url: string }>(`/inventory/photo-url?key=${encodeURIComponent(key)}`),
};
